import { Album, ArrowLeft, ChevronDown, ChevronUp, Layers, LayoutGrid, Package, RefreshCw, Save, Scissors } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getShop } from '../../../api/shop.api';
import useServiceTabsStore, { buildDefaultTabs } from '../../../stores/serviceTabsStore';
import { useAuthStore } from '../../../stores/authStore';

// Icon cho từng danh mục (đối chiếu app: cut/cube/layers/albums)
const TAB_ICONS = {
    dichvu: Scissors,
    sanpham: Package,
    combo: Layers,
    lieutrinh: Album,
};

export default function ServiceTabsSettings() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const tabs = useServiceTabsStore((s) => s.tabs);
    const setTabs = useServiceTabsStore((s) => s.setTabs);

    const [draftTabs, setDraftTabs] = useState(() => [...tabs].sort((a, b) => a.order - b.order));
    const [shopType, setShopType] = useState(null);
    const [savedMsg, setSavedMsg] = useState('');

    // Lấy ngành của shop để nút "Mặc định" khôi phục đúng thứ tự theo ngành
    useEffect(() => {
        if (!user?.shopId) return;
        let mounted = true;
        getShop(user.shopId)
            .then(({ data }) => {
                if (mounted) setShopType(data?.shopType ?? null);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, [user?.shopId]);

    const hasChanges = useMemo(() => {
        return draftTabs.some((t) => {
            const orig = tabs.find((o) => o.key === t.key);
            return t.visible !== orig?.visible || t.order !== orig?.order;
        });
    }, [tabs, draftTabs]);

    const onToggle = (key) => {
        setDraftTabs((prev) => prev.map((t) => (t.key === key ? { ...t, visible: !t.visible } : t)));
    };

    const onMoveUp = (key) => {
        setDraftTabs((prev) => {
            const sorted = [...prev].sort((a, b) => a.order - b.order);
            const idx = sorted.findIndex((t) => t.key === key);
            if (idx <= 0) return prev;
            const newTabs = [...sorted];
            const prevOrder = newTabs[idx - 1].order;
            const curOrder = newTabs[idx].order;
            newTabs[idx - 1] = { ...newTabs[idx - 1], order: curOrder };
            newTabs[idx] = { ...newTabs[idx], order: prevOrder };
            return newTabs.sort((a, b) => a.order - b.order);
        });
    };

    const onMoveDown = (key) => {
        setDraftTabs((prev) => {
            const sorted = [...prev].sort((a, b) => a.order - b.order);
            const idx = sorted.findIndex((t) => t.key === key);
            if (idx >= sorted.length - 1) return prev;
            const newTabs = [...sorted];
            const nextOrder = newTabs[idx + 1].order;
            const curOrder = newTabs[idx].order;
            newTabs[idx + 1] = { ...newTabs[idx + 1], order: curOrder };
            newTabs[idx] = { ...newTabs[idx], order: nextOrder };
            return newTabs.sort((a, b) => a.order - b.order);
        });
    };

    const handleResetDefault = () => {
        const ok = window.confirm(
            'Khôi phục thứ tự và hiển thị danh mục theo ngành của bạn? Các thay đổi hiện tại sẽ bị ghi đè.',
        );
        if (!ok) return;
        const defaults = buildDefaultTabs(shopType);
        setTabs(defaults);
        setDraftTabs([...defaults].sort((a, b) => a.order - b.order));
        setSavedMsg('Đã đặt về cấu hình mặc định theo ngành');
        setTimeout(() => setSavedMsg(''), 2000);
    };

    const handleSave = () => {
        if (!hasChanges) {
            navigate(-1);
            return;
        }
        setTabs(draftTabs);
        setSavedMsg('Đã lưu cấu hình danh mục');
        setTimeout(() => navigate(-1), 600);
    };

    const sortedDraft = [...draftTabs].sort((a, b) => a.order - b.order);

    return (
        <div style={S.page}>
            <button style={S.backBtn} onClick={() => navigate(-1)}>
                <ArrowLeft size={16} /> Quay lại
            </button>

            {/* Hero */}
            <div style={S.heroCard}>
                <div>
                    <div style={S.heroCaption}>Trạng thái cấu hình</div>
                    <div style={S.heroTitle}>Danh mục sản phẩm</div>
                    <div style={S.statusRow}>
                        <span style={S.statusDot} />
                        <span style={S.statusText}>ĐANG ÁP DỤNG</span>
                    </div>
                </div>
                <div style={S.heroIconWrap}>
                    <LayoutGrid size={18} color="#7a8a9a" />
                </div>
            </div>

            {/* Section */}
            <div style={S.sectionBlock}>
                <div style={S.sectionLabel}>THỨ TỰ VÀ HIỂN THỊ</div>
                <div style={S.sectionHint}>Bật/tắt danh mục và dùng nút ▲▼ để đổi thứ tự hiển thị</div>

                <div style={S.itemsContainer}>
                    {sortedDraft.map((tab, index) => {
                        const Icon = TAB_ICONS[tab.key] || Package;
                        const isFirst = index === 0;
                        const isLast = index === sortedDraft.length - 1;
                        return (
                            <div key={tab.key} style={S.item}>
                                <div style={S.itemLeft}>
                                    <div style={S.iconWrap}>
                                        <Icon size={16} color="#0b78b8" />
                                    </div>
                                    <span style={S.itemTitle}>{tab.label}</span>
                                </div>
                                <div style={S.itemRight}>
                                    <div style={S.orderButtons}>
                                        <button
                                            style={{ ...S.orderBtn, ...(isFirst ? S.orderBtnDisabled : null) }}
                                            onClick={() => onMoveUp(tab.key)}
                                            disabled={isFirst}
                                        >
                                            <ChevronUp size={16} color={isFirst ? '#c5ccd4' : '#374151'} />
                                        </button>
                                        <button
                                            style={{ ...S.orderBtn, ...(isLast ? S.orderBtnDisabled : null) }}
                                            onClick={() => onMoveDown(tab.key)}
                                            disabled={isLast}
                                        >
                                            <ChevronDown size={16} color={isLast ? '#c5ccd4' : '#374151'} />
                                        </button>
                                    </div>
                                    <button
                                        style={{
                                            ...S.switchTrack,
                                            ...(tab.visible ? S.switchTrackOn : S.switchTrackOff),
                                        }}
                                        onClick={() => onToggle(tab.key)}
                                    >
                                        <span
                                            style={{
                                                ...S.switchThumb,
                                                ...(tab.visible ? S.switchThumbOn : S.switchThumbOff),
                                            }}
                                        />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {savedMsg && <div style={S.savedBanner}>{savedMsg}</div>}

            {/* Footer actions */}
            <div style={S.footer}>
                <button style={S.resetButton} onClick={handleResetDefault}>
                    <RefreshCw size={16} color="#1f2937" /> Mặc định
                </button>
                <button
                    style={{ ...S.saveButton, ...(hasChanges ? null : S.saveButtonDisabled) }}
                    onClick={handleSave}
                >
                    <Save size={16} color="#fff" /> {hasChanges ? 'Lưu thay đổi' : 'Không có thay đổi'}
                </button>
            </div>
        </div>
    );
}

const S = {
    page: { maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' },
    backBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
        border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff',
        padding: '8px 14px', fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer',
    },
    heroCard: {
        borderRadius: 16, padding: '14px 16px', marginBottom: 16,
        background: '#d9ecff', border: '1px solid #c3dffc',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    heroCaption: { fontSize: 12, color: '#70839a', marginBottom: 4 },
    heroTitle: { fontSize: 28, lineHeight: '32px', fontWeight: 800, color: '#173654' },
    statusRow: { display: 'flex', alignItems: 'center', marginTop: 10, gap: 6 },
    statusDot: { width: 7, height: 7, borderRadius: 4, background: '#0e83cd', display: 'inline-block' },
    statusText: { fontSize: 12, fontWeight: 800, letterSpacing: 0.4, color: '#2e7eb5' },
    heroIconWrap: {
        width: 40, height: 40, borderRadius: 10, background: '#eef6ff',
        border: '1px solid #d7e8fa', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    sectionBlock: { marginBottom: 14 },
    sectionLabel: { fontSize: 15, fontWeight: 800, color: '#404750', letterSpacing: 0.5, marginBottom: 4, marginLeft: 4 },
    sectionHint: { fontSize: 13, color: '#6b7280', marginBottom: 10, marginLeft: 4 },
    itemsContainer: { display: 'flex', flexDirection: 'column', gap: 10 },
    item: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: '#fff', borderRadius: 12, padding: '13px 12px', border: '1px solid #eef0f3',
    },
    itemLeft: { display: 'flex', alignItems: 'center', flex: 1, gap: 10 },
    itemRight: { display: 'flex', alignItems: 'center', gap: 12 },
    iconWrap: {
        width: 28, height: 28, borderRadius: 8, background: '#f2f6fb',
        border: '1px solid #e8eef7', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    itemTitle: { fontSize: 16, fontWeight: 700, color: '#111827' },
    orderButtons: { display: 'flex', gap: 4 },
    orderBtn: {
        width: 28, height: 28, borderRadius: 8, background: '#f3f4f6',
        border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    },
    orderBtnDisabled: { background: '#f9fafb', borderColor: '#f3f4f6', cursor: 'not-allowed' },
    switchTrack: {
        width: 44, height: 26, borderRadius: 13, display: 'flex', alignItems: 'center',
        padding: '0 3px', border: 'none', cursor: 'pointer',
    },
    switchTrackOn: { background: '#0089F6', justifyContent: 'flex-end' },
    switchTrackOff: { background: '#d1d5db', justifyContent: 'flex-start' },
    switchThumb: {
        width: 20, height: 20, borderRadius: 10, background: '#fff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.15)', display: 'inline-block',
    },
    switchThumbOn: {},
    switchThumbOff: {},
    savedBanner: {
        margin: '4px 0 12px', padding: '10px 14px', borderRadius: 10,
        background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 14, fontWeight: 600,
    },
    footer: { display: 'flex', gap: 10, marginTop: 16 },
    resetButton: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '13px 16px',
        borderRadius: 12, background: '#fff', border: '1px solid #e5e7eb',
        fontSize: 15, fontWeight: 600, color: '#1f2937', cursor: 'pointer',
    },
    saveButton: {
        flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '13px', borderRadius: 12, background: '#0089F6', border: 'none',
        fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer',
    },
    saveButtonDisabled: { background: '#93c5fd', cursor: 'default' },
};
